/**
 * Developer Runs tab — lists all runs from the v2 API.
 *
 * Features: status filter dropdown, copy-to-clipboard on IDs,
 * hover tooltips on truncated cells, limit query param.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { env } from "@/env";
import { getAccessToken } from "@/lib/auth/storage";

export const Route = createFileRoute("/_dashboard/developer/runs")({
	component: DeveloperRunsPage,
});

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive"> = {
	completed: "default",
	running: "secondary",
	failed: "destructive",
	cancelled: "secondary",
};

const STATUS_OPTIONS = ["all", "running", "completed", "failed", "cancelled"] as const;

function useV2Runs(status: string) {
	return useQuery({
		queryKey: ["developer", "runs", status],
		queryFn: async () => {
			const token = getAccessToken();
			const params = new URLSearchParams({ limit: "50" });
			if (status !== "all") params.set("status", status);
			const res = await fetch(`${env.VITE_API_BASE_URL}/api/v2/runs/?${params}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			const body = await res.json();
			return body.data;
		},
		staleTime: 30_000,
	});
}

/** Copy text to clipboard with brief visual feedback. */
function CopyCell({ value }: { value: string }) {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type="button"
			className="cursor-pointer font-mono text-xs hover:text-primary"
			title={`Click to copy: ${value}`}
			onClick={() => {
				navigator.clipboard.writeText(value);
				setCopied(true);
				setTimeout(() => setCopied(false), 1200);
			}}
		>
			{copied ? "copied!" : value}
		</button>
	);
}

function DeveloperRunsPage() {
	const [status, setStatus] = useState("all");
	const { data: runs, isLoading } = useV2Runs(status);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<Select value={status} onValueChange={setStatus}>
					<SelectTrigger className="w-40">
						<SelectValue placeholder="Filter by status" />
					</SelectTrigger>
					<SelectContent>
						{STATUS_OPTIONS.map((s) => (
							<SelectItem key={s} value={s}>
								{s === "all" ? "All statuses" : s}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{isLoading ? (
				<div className="space-y-3">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
			) : !runs?.length ? (
				<div className="py-16 text-center text-muted-foreground">No runs found</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>ID</TableHead>
							<TableHead>Teammate</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>User ID</TableHead>
							<TableHead>Output</TableHead>
							<TableHead>Created</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{runs.map((r: Record<string, unknown>) => (
							<TableRow key={String(r.id)}>
								<TableCell>
									<CopyCell value={String(r.id)} />
								</TableCell>
								<TableCell className="font-mono text-xs">
									{r.teammate_id ? String(r.teammate_id) : "—"}
								</TableCell>
								<TableCell>
									<Badge variant={STATUS_VARIANT[String(r.status)] || "secondary"}>
										{String(r.status)}
									</Badge>
								</TableCell>
								<TableCell className="text-muted-foreground">
									{r.user_id ? String(r.user_id) : "—"}
								</TableCell>
								<TableCell
									className="max-w-xs truncate text-muted-foreground"
									title={r.output ? String(r.output) : undefined}
								>
									{r.output ? String(r.output).slice(0, 60) : "—"}
								</TableCell>
								<TableCell className="text-muted-foreground text-xs">
									{new Date(String(r.created_at)).toLocaleDateString()}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);
}
